import { useQuery } from "@tanstack/react-query";
import Cookies from "js-cookie";
import { useMemo } from "react";
import { format } from "date-fns";
import { Line } from "react-chartjs-2";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
} from "chart.js";
import "chart.js/auto";
import zoomPlugin from "chartjs-plugin-zoom";
import "chartjs-adapter-moment";

import {
  getAllAdminsActivityQueryOptions,
  getAllAdminsQueryOptions,
} from "@/config/queries/superadmin-query-options";
import { convertUTCtoIST } from "@/utils";

// Register the zoom plugin
ChartJS.register(zoomPlugin);

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend
);

function getTotalSecondsFromDate(d: string): number {
  // Convert the input date string to a Date object
  const date = new Date(d);

  // Get hours, minutes, and seconds from the IST date
  const hours = date.getHours();
  const minutes = date.getMinutes();
  const seconds = date.getSeconds();

  // Calculate total seconds
  const totalSeconds = hours * 3600 + minutes * 60 + seconds;

  return totalSeconds;
}

function secondsToTimeString(seconds: number | null): string {
  if (seconds === null) {
    return "Not Found";
  }
  // Calculate hours, minutes, and determine AM/PM
  const hours = Math.floor(seconds / 3600) % 24;
  const minutes = Math.floor((seconds % 3600) / 60);
  const period = hours >= 12 ? "PM" : "AM";

  // Adjust hours for PM notation
  const adjustedHours = hours % 12 === 0 ? 12 : hours % 12;

  // Format the hours and minutes as HH:mm with AM/PM notation
  const formattedHours = String(adjustedHours).padStart(2, "0");
  const formattedMinutes = String(minutes).padStart(2, "0");

  // Return the formatted time string
  return `${formattedHours}:${formattedMinutes}${period}`;
}

const AdminsActivityForSuperAdmin = () => {
  const user = Cookies.get("key") as string;

  const { data, isPending, isFetching } = useQuery(
    getAllAdminsQueryOptions(user)
  );

  const adminsData = useMemo(() => {
    if (isPending || isFetching) {
      return [];
    }

    if (data === undefined || data?.admins === undefined) {
      return [];
    }

    const result = data?.admins?.map((admin) => {
      return {
        id: admin?._id,
        name: admin?.name,
      };
    });

    return result;
  }, [data, isPending, isFetching]);

  const startdate = useMemo(() => new Date(), []);
  const enddate = useMemo(() => new Date(), []);

  const {
    data: adminAllActivities,
    isPending: isAllActivitiesPending,
    isFetching: isAllActivitiesFetching,
  } = useQuery(
    getAllAdminsActivityQueryOptions(
      user,
      convertUTCtoIST(new Date(startdate.setHours(0))),
      convertUTCtoIST(new Date(enddate.setHours(23)))
    )
  );

  const admins = useMemo(() => {
    if (isPending || isAllActivitiesPending) {
      return [];
    }

    if (
      adminAllActivities === undefined ||
      adminAllActivities?.activity === undefined
    ) {
      return [];
    }

    const logins = adminAllActivities?.activity?.filter(
      (entry) => entry?.mode === "LOGIN"
    );

    const logouts = adminAllActivities?.activity?.filter(
      (entry) => entry?.mode === "LOGOUT"
    );

    const pairedLogs: {
      inLog: {
        mode: string;
        createdAt: string;
        admin: {
          _id: string;
          name: string;
        };
      };
      outLog: {
        mode: string;
        createdAt: string;
        admin?: {
          _id: string;
          name: string;
        };
      } | null;
    }[] = [];

    for (const inLog of logins) {
      const correspondingOutLog = logouts?.find(
        (outlog) =>
          new Date(outlog?.createdAt).getDate() ===
            new Date(inLog?.createdAt).getDate() &&
          !pairedLogs.some((pair) => pair.outLog === outlog)
      );

      if (correspondingOutLog) {
        pairedLogs.push({
          inLog,
          outLog: correspondingOutLog,
        });
      } else {
        pairedLogs.push({
          inLog,
          outLog: null,
        });
      }
    }

    let result: {
      id: string;
      name: string;
      loginTime: number;
      logoutTime: number | undefined;
      date: string;
      borderColor: string;
      y: number;
    }[] = [];

    let helper = 5;

    for (const pair of pairedLogs) {
      if (pair.outLog !== null) {
        const loginTime = getTotalSecondsFromDate(pair.inLog?.createdAt);
        const logoutTime = getTotalSecondsFromDate(pair.outLog?.createdAt);
        const date = format(new Date(pair.inLog?.createdAt), "PP");

        const already = result?.find(
          (res) => res.id === pair.inLog?.admin?._id
        );

        if (already) {
          result.push({
            loginTime,
            logoutTime,
            date,
            id: pair.inLog?.admin?._id,
            name: pair.inLog?.admin?.name,
            borderColor: "rgba(54, 162, 235, 1)",
            y: already?.y,
          });
        } else {
          result.push({
            loginTime,
            logoutTime,
            date,
            id: pair.inLog?.admin?._id,
            name: pair.inLog?.admin?.name,
            borderColor: "rgba(54, 162, 235, 1)",
            y: helper,
          });

          helper = helper + 5;
        }
      } else {
        const loginTime = getTotalSecondsFromDate(pair.inLog?.createdAt);
        const logoutTime = undefined;
        const date = format(new Date(pair.inLog?.createdAt), "PP");

        const already = result?.find(
          (res) => res.id === pair.inLog?.admin?._id
        );

        if (already) {
          result.push({
            loginTime,
            logoutTime,
            date,
            id: pair.inLog?.admin?._id,
            name: pair.inLog?.admin?.name,
            borderColor: "rgba(54, 162, 235, 1)",
            y: already?.y,
          });
        } else {
          result.push({
            loginTime,
            logoutTime,
            date,
            id: pair.inLog?.admin?._id,
            name: pair.inLog?.admin?.name,
            borderColor: "rgba(54, 162, 235, 1)",
            y: helper,
          });

          helper = helper + 5;
        }
      }
    }

    return result;
  }, [data, isAllActivitiesPending, isAllActivitiesFetching]);

  const labels2 = [
    // 8:00am
    28800,

    // 9:00am
    32400,

    // 10:00am
    36000,

    // 11:00am
    39600,

    // 12:00pm
    43200,

    // 1:00pm
    46800,

    // 2:00pm
    50400,

    // 3:00pm
    54000,

    // 4:00pm
    57600,

    // 5:00pm
    61200,

    // 6:00pm
    64800,

    // 7:00pm
    68400,

    // 8:00pm
    72000,
  ];

  const completeChartData = useMemo(() => {
    let refinedLabels = labels2;

    admins.forEach((emp) => {
      if (!refinedLabels.includes(emp.loginTime)) {
        refinedLabels.push(emp.loginTime);
      }
      if (emp.logoutTime && !refinedLabels.includes(emp.logoutTime)) {
        refinedLabels.push(emp.logoutTime);
      }
    });

    refinedLabels = refinedLabels.sort((a, b) => a - b);

    let datasets: {
      id: string;
      label: string;
      borderColor: string;
      data: {
        x: number | undefined;
        y: number | undefined;
      }[];
    }[] = adminsData.map((emp) => {
      return {
        id: emp?.id,
        label: emp?.name,
        data: [],
        borderColor: "",
      };
    });

    admins.forEach((emp) => {
      const isAlreadyExist = datasets?.find((d) => d?.id === emp?.id);
      if (!isAlreadyExist) {
        datasets.push({
          id: emp.id,
          label: emp.name,
          borderColor: emp.borderColor,
          data: [
            {
              x: emp.loginTime,
              y: emp.y,
            },
            {
              x: emp.logoutTime,
              y: emp.y,
            },
          ],
        });
      } else {
        datasets = datasets.map((dataset) => {
          if (dataset.id === isAlreadyExist.id) {
            return {
              ...isAlreadyExist,
              data: [
                ...isAlreadyExist.data,
                { x: undefined, y: undefined },
                {
                  x: emp.loginTime,
                  y: emp.y,
                },
                {
                  x: emp.logoutTime,
                  y: emp.y,
                },
              ],
            };
          }

          return dataset;
        });
      }
    });

    return {
      labels: refinedLabels,
      datasets,
    };
  }, [admins, labels2, adminsData, isPending, isFetching]);

  const yMapping: {
    [key: string]: string;
  } = useMemo(() => {
    const result: {
      [key: string]: string;
    } = {};

    for (const admin of admins) {
      const computed = String(admin?.y);
      result[computed] = admin.name;
    }

    return result;
  }, [admins]);

  return (
    <div className="p-7 h-[80vh]">
      <Line
        data={completeChartData}
        options={{
          scales: {
            y: {
              min: 0,
              ticks: {
                callback: (value) => {
                  const find = yMapping[String(value)];
                  return find;
                },
              },
            },
            x: {
              ticks: {
                callback: (value) => {
                  const find = completeChartData.labels[value as number];

                  return secondsToTimeString(find);
                },
              },
            },
          },
          responsive: true,
          maintainAspectRatio: false,
          plugins: {
            zoom: {
              pan: {
                enabled: true,
                mode: "x",
              },
              zoom: {
                wheel: {
                  enabled: true,
                },
                pinch: {
                  enabled: true,
                },
                mode: "x",
              },
            },
            tooltip: {
              callbacks: {
                title: (tooltipItems) => {
                  const dataindex = tooltipItems[0].dataIndex;
                  const datasetIndex = tooltipItems[0].datasetIndex;
                  const dataset = completeChartData.datasets[datasetIndex];
                  const find = dataset.data?.find((_, i) => i === dataindex);

                  return secondsToTimeString(find!.x as number);
                },
              },
            },
          },
        }}
      />
    </div>
  );
};

export default AdminsActivityForSuperAdmin;
